//
// Copyright © 2026 Hardcore Engineering Inc.
//
// Licensed under the Eclipse Public License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License. You may
// obtain a copy of the License at https://www.eclipse.org/legal/epl-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
//
// See the License for the specific language governing permissions and
// limitations under the License.
//

/**
 * @public
 */
export interface NumericInputOptions {
  /** Allow a minus sign. */
  negative?: boolean
  /** Allow a decimal separator ("." or ","). Default true. */
  decimal?: boolean
}

function allowedChars (options: NumericInputOptions): RegExp {
  const decimal = options.decimal !== false ? '.,' : ''
  const minus = options.negative === true ? '\\-' : ''
  return new RegExp(`^[0-9${decimal}${minus}]*$`)
}

// Spaces, no-break and thin spaces: how amounts are printed ("12 107 680").
const GROUPING = /[\s  ]/g

/**
 * Svelte action for number fields: only digits (and a decimal separator,
 * and a minus sign when allowed) can be typed or pasted.
 *
 * `<input type="number">` alone is not enough: Chrome still accepts "e", "+"
 * and "-" (exponent syntax) and Firefox accepts any text. The field then
 * reports an empty value, which callers read as 0, so a stray letter in a
 * salary could save $0. A pasted amount keeps its digits ("12 107 680").
 *
 * @public
 */
export function numericInput (
  node: HTMLInputElement,
  options: NumericInputOptions = {}
): { update: (options: NumericInputOptions) => void, destroy: () => void } {
  let allowed = allowedChars(options)
  let opts = options

  function onBeforeInput (e: InputEvent): void {
    // Deletions, undo and paste carry no data here; paste is handled below.
    if (e.data == null) return
    if (!allowed.test(e.data)) e.preventDefault()
  }

  function onKeyDown (e: KeyboardEvent): void {
    if (e.ctrlKey || e.metaKey || e.altKey) return
    if (e.key.length === 1 && !allowed.test(e.key)) e.preventDefault()
  }

  function onPaste (e: ClipboardEvent): void {
    const text = e.clipboardData?.getData('text') ?? ''
    const cleaned = text.replace(GROUPING, '')
    const pattern = opts.decimal !== false ? /^-?\d+([.,]\d+)?$/ : /^-?\d+$/
    e.preventDefault()
    if (!pattern.test(cleaned) || (opts.negative !== true && cleaned.startsWith('-'))) return
    node.value = cleaned.replace(',', '.')
    node.dispatchEvent(new Event('input', { bubbles: true }))
  }

  node.addEventListener('beforeinput', onBeforeInput)
  node.addEventListener('keydown', onKeyDown)
  node.addEventListener('paste', onPaste)

  return {
    update (next: NumericInputOptions) {
      opts = next
      allowed = allowedChars(next)
    },
    destroy () {
      node.removeEventListener('beforeinput', onBeforeInput)
      node.removeEventListener('keydown', onKeyDown)
      node.removeEventListener('paste', onPaste)
    }
  }
}

/**
 * @public
 */
export interface AmountInputOptions {
  /** Allow a minus sign. */
  negative?: boolean
  /** Digits after the point: 2 (tiyin, cents) by default, 0 for whole numbers. */
  decimals?: number
}

// Amounts are printed with a narrow no-break space between thousands and a
// point before tiyin or cents (DESIGN.md §7.3); fields show them the same way.
const AMOUNT_GROUP = ' '
const AMOUNT_CHAR = /[0-9.-]/

/**
 * What an amount field shows for typed text: "1234567,5" → "1 234 567.5".
 * A comma is read as the point, digits past `decimals` are dropped, and what
 * is still being typed stays ("12.", "-").
 *
 * @public
 */
export function formatAmountText (text: string, options: AmountInputOptions = {}): string {
  const decimals = options.decimals ?? 2
  const s = text.replace(/,/g, '.').replace(/[^0-9.-]/g, '')
  const negative = options.negative === true && s.startsWith('-')
  const [intPart, ...rest] = s.replace(/-/g, '').split('.')
  const hasPoint = rest.length > 0 && decimals > 0
  let int = intPart.replace(/^0+(?=\d)/, '')
  if (hasPoint && int === '') int = '0'
  const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, AMOUNT_GROUP)
  return (negative ? '-' : '') + grouped + (hasPoint ? '.' + rest.join('').slice(0, decimals) : '')
}

/**
 * A stored amount as an amount field shows it: whole amounts bare
 * ("14 000 000"), others with every decimal ("14 000 000.50").
 *
 * @public
 */
export function amountFieldText (value: number | null | undefined, options: AmountInputOptions = {}): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return ''
  const decimals = options.decimals ?? 2
  const fixed = Math.abs(value).toFixed(decimals)
  const whole = decimals === 0 || /\.0+$/.test(fixed)
  const body = formatAmountText(whole ? fixed.replace(/\.\d*$/, '') : fixed, { decimals })
  return value < 0 && Number(fixed) !== 0 ? `-${body}` : body
}

/** Where the caret goes after `count` digits, points and minus signs. */
function caretAfter (text: string, count: number): number {
  if (count <= 0) return 0
  let seen = 0
  for (let i = 0; i < text.length; i++) {
    if (AMOUNT_CHAR.test(text[i]) && ++seen === count) return i + 1
  }
  return text.length
}

function countAmountChars (text: string): number {
  let n = 0
  for (const ch of text) if (AMOUNT_CHAR.test(ch)) n++
  return n
}

/**
 * Svelte action for money fields (`<input type="text">`): only digits, one
 * point (a typed comma becomes one) and, when allowed, a minus sign get in,
 * and the value is grouped as it is typed: "14 000 000.50". The caret stays
 * after the character just typed. Read the value with `enteredNumber()`.
 *
 * @public
 */
export function amountInput (
  node: HTMLInputElement,
  options: AmountInputOptions = {}
): { update: (options: AmountInputOptions) => void, destroy: () => void } {
  let opts = options

  function allowed (ch: string): boolean {
    if (/[0-9]/.test(ch)) return true
    if (ch === '.' || ch === ',') return (opts.decimals ?? 2) > 0
    return ch === '-' && opts.negative === true
  }

  function onBeforeInput (e: InputEvent): void {
    const back = e.inputType === 'deleteContentBackward'
    if (back || e.inputType === 'deleteContentForward') {
      // Deleting a group space deletes the digit beyond it instead: the
      // space alone would only come back.
      const at = node.selectionStart ?? 0
      if (at !== node.selectionEnd) return
      const space = back ? at - 1 : at
      const digit = back ? at - 2 : at + 1
      if (node.value[space] !== AMOUNT_GROUP || digit < 0 || digit >= node.value.length) return
      e.preventDefault()
      node.value = node.value.slice(0, digit) + node.value.slice(digit + 1)
      node.setSelectionRange(back ? digit : at, back ? digit : at)
      node.dispatchEvent(new Event('input', { bubbles: true }))
      return
    }
    if (e.data == null) return
    if (![...e.data].every(allowed)) e.preventDefault()
  }

  function onKeyDown (e: KeyboardEvent): void {
    if (e.ctrlKey || e.metaKey || e.altKey) return
    if (e.key.length === 1 && !allowed(e.key)) e.preventDefault()
  }

  // Runs in the capture phase, so the field's own input handlers and
  // `bind:value` already see the grouped text.
  function onInput (): void {
    const raw = node.value
    const caret = node.selectionStart ?? raw.length
    const formatted = formatAmountText(raw, opts)
    if (formatted === raw) return
    const before = countAmountChars(formatAmountText(raw.slice(0, caret), opts))
    node.value = formatted
    if (document.activeElement === node) {
      const pos = caretAfter(formatted, before)
      node.setSelectionRange(pos, pos)
    }
  }

  function onPaste (e: ClipboardEvent): void {
    const text = (e.clipboardData?.getData('text') ?? '').replace(GROUPING, '')
    e.preventDefault()
    const pattern = (opts.decimals ?? 2) > 0 ? /^-?\d+([.,]\d+)?$/ : /^-?\d+$/
    if (!pattern.test(text) || (opts.negative !== true && text.startsWith('-'))) return
    node.setRangeText(text, node.selectionStart ?? 0, node.selectionEnd ?? node.value.length, 'end')
    node.dispatchEvent(new Event('input', { bubbles: true }))
  }

  node.addEventListener('beforeinput', onBeforeInput)
  node.addEventListener('keydown', onKeyDown)
  node.addEventListener('input', onInput, true)
  node.addEventListener('paste', onPaste)
  // A value set before the action ran ("14000000") is shown grouped too.
  if (node.value !== '') node.value = formatAmountText(node.value, opts)

  return {
    update (next: AmountInputOptions) {
      opts = next
    },
    destroy () {
      node.removeEventListener('beforeinput', onBeforeInput)
      node.removeEventListener('keydown', onKeyDown)
      node.removeEventListener('input', onInput, true)
      node.removeEventListener('paste', onPaste)
    }
  }
}

/**
 * The number a field holds, or undefined when it was cleared or holds no
 * number (so a caller can skip the save instead of writing 0). Accepts the
 * printed form too: "12 107 680", "4,5", "14 000 000.50".
 *
 * @public
 */
export function enteredNumber (value: unknown, bounds: { min?: number, max?: number } = {}): number | undefined {
  if (value === null || value === undefined || value === '') return undefined
  const n = typeof value === 'number' ? value : Number(String(value).replace(GROUPING, '').replace(',', '.'))
  if (!Number.isFinite(n)) return undefined
  if (bounds.min !== undefined && n < bounds.min) return undefined
  if (bounds.max !== undefined && n > bounds.max) return undefined
  return n
}
